import React, { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { AnimatePresence, motion } from "framer-motion"
import { z } from "zod"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form"
import { toast } from "sonner"
import { useRouter } from '@tanstack/react-router'
import { ChevronRight, Store } from 'lucide-react'

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
})

type EmailFormData = z.infer<typeof emailSchema>

const Index = () => {
  const [isLoading, setIsLoading] = useState(false)
  const router = useRouter()

  const form = useForm<EmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: '',
    }
  })

  const onSubmit = async (data: EmailFormData) => {
    try {
      setIsLoading(true)
      // TODO: Implement forgot password API call
      toast.success('Reset password instructions sent to your email')
      router.navigate({
        to: "/login"
      })
    } catch (error: any) {
      console.error("Forgot password error:", error)
      toast.error(error?.message || 'An error occurred. Please try again later.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="relative min-h-screen w-full flex items-center justify-center overflow-hidden">
      <div className="absolute inset-0 bg-[#19213D]">
        <div 
          className="absolute w-[150%] h-[150%] -top-[25%] -left-[25%] opacity-80"
          style={{
            background: `conic-gradient(from 45deg at 50% 50%,#474DFF 0deg,transparent 60deg,#474DFF 180deg,transparent 240deg,#474DFF 360deg)`,
            transform: 'rotate(-12deg)',
            filter: 'blur(60px)'
          }}
        />
      </div>
      
      <div className="relative z-10 flex flex-col items-center gap-8">
        <AnimatePresence mode="wait">
          <motion.div
            key="forgot-password-logos"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.3 }}
            className="flex items-center gap-4"
          >
            <img src="/logo.svg" alt="Logo" className="w-12 h-auto" />
            <div className="flex items-center gap-[-8px]">
              <ChevronRight className="w-6 h-6 text-white" />
              <ChevronRight className="w-6 h-6 text-white -ml-4" />
              <ChevronRight className="w-6 h-6 text-white -ml-4" />
            </div>
            <div className="w-12 h-12 rounded-full bg-[#2C2C35] flex items-center justify-center">
              <Store className="w-6 h-6 text-white" />
            </div>
          </motion.div>
        </AnimatePresence>
        
        <AnimatePresence mode="wait">
          <motion.div
            key="forgot-password"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            className="relative z-10"
          >
            <Card className="w-[400px] h-[320px] bg-[#1C1C24] border-0">
              <CardHeader>
                <CardTitle className="text-2xl font-semibold text-white text-center">Forgot Password</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <Label className="text-sm text-gray-300">Email</Label>
                          <FormControl>
                            <Input 
                              {...field}
                              type="email"
                              className="bg-[#2C2C35] border-gray-700 text-white focus:border-[#474DFF] focus:ring-[#474DFF]"
                              placeholder="Enter your email"
                            />
                          </FormControl>
                          <FormMessage className="text-red-500" />
                        </FormItem>
                      )}
                    />

                    <Button 
                      type="submit"
                      disabled={isLoading}
                      className="w-full bg-[#09090B] text-white hover:bg-[#09090B]/90"
                    >
                      {isLoading ? 'Sending...' : 'Send Reset Instructions'}
                    </Button>

                    <div className="text-center">
                      <Button
                        type="button"
                        variant="link"
                        className="text-gray-400 hover:text-white"
                        onClick={() => router.navigate({ to: "/login" })}
                      >
                        Back to Login
                      </Button>
                    </div>
                  </form>
                </Form>
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  )
}

export default Index 